/**
 * The trust boundary. Every uploaded file passes through here before any
 * parser touches it (CODE_RULES.md §7).
 *
 * A local file picker is still untrusted input: the user may pick the wrong
 * file, a renamed file, or a hostile one. We check size before reading, and
 * sniff magic bytes rather than believing the extension or the browser's MIME
 * guess — both are trivially wrong and neither is authoritative.
 */
import { FILE_SIGNATURES, UPLOAD_LIMITS } from "../core/config";
import { appError, err, ok, type Result } from "../core/result";

export type UploadKind = "ifc" | "pdf";

interface Spec {
  readonly maxBytes: number;
  readonly signature: string;
  readonly label: string;
}

const SPECS: Record<UploadKind, Spec> = {
  ifc: {
    maxBytes: UPLOAD_LIMITS.MAX_IFC_BYTES,
    signature: FILE_SIGNATURES.IFC_SPF,
    label: "IFC",
  },
  pdf: {
    maxBytes: UPLOAD_LIMITS.MAX_PDF_BYTES,
    signature: FILE_SIGNATURES.PDF,
    label: "PDF",
  },
};

function formatMegabytes(bytes: number): string {
  return `${String(Math.round(bytes / (1024 * 1024)))} MB`;
}

/**
 * Identifies a file by its contents, not its name.
 *
 * Filenames are unreliable on mobile: iOS and Android pickers may hand over a
 * file with a rewritten name or no extension at all, and iOS in particular
 * cannot express an `.ifc` filter. Sniffing the header is both more robust and
 * the same check we would have to do anyway.
 */
export async function detectUploadKind(file: File): Promise<UploadKind | null> {
  const head = new Uint8Array(await file.slice(0, FILE_SIGNATURES.SNIFF_BYTES).arrayBuffer());
  for (const kind of ["ifc", "pdf"] as const) {
    if (hasSignature(head, SPECS[kind].signature)) return kind;
  }
  return null;
}

/**
 * Validates the file and returns its bytes. Reading happens here, once, so a
 * caller cannot accidentally parse an unvalidated buffer.
 */
export async function readValidatedFile(file: File, kind: UploadKind): Promise<Result<Uint8Array>> {
  const spec = SPECS[kind];

  if (file.size === 0) {
    return err(appError("FILE_EMPTY", `That ${spec.label} file is empty.`));
  }

  // Check size BEFORE reading, so an oversized file never enters memory.
  if (file.size > spec.maxBytes) {
    return err(
      appError(
        "FILE_TOO_LARGE",
        `That ${spec.label} is ${formatMegabytes(file.size)}. The limit is ${formatMegabytes(spec.maxBytes)}.`,
      ),
    );
  }

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.arrayBuffer());
  } catch (cause) {
    return err(appError("FILE_UNREADABLE", `That ${spec.label} file could not be read.`, cause));
  }

  if (!hasSignature(bytes, spec.signature)) {
    return err(appError("FILE_WRONG_TYPE", `That does not look like a valid ${spec.label} file.`));
  }

  return ok(bytes);
}

/**
 * Latin-1 decode of the leading bytes, then substring search. We search rather
 * than compare at offset 0 because IFC files may legitimately begin with a
 * UTF-8 BOM or leading whitespace before `ISO-10303-21`.
 */
function hasSignature(bytes: Uint8Array, signature: string): boolean {
  const head = bytes.subarray(0, FILE_SIGNATURES.SNIFF_BYTES);
  let text = "";
  for (const byte of head) {
    text += String.fromCharCode(byte);
  }
  return text.includes(signature);
}
